import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  message,
} from "antd";
import styled from "@emotion/styled";
import { supabase } from "../../lib/supabase";
import { AuthError } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../hooks/useUser";
import { useIsAgent } from "../../hooks/useIsAgent";
import { AGENT_ROUTES } from "../../config/agentPortal";

const { Title, Paragraph } = Typography;

const AuthContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f0f2f5;
  padding: 1rem;
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 420px;
`;

const Logo = styled.img`
  height: 36px;
  display: block;
  margin: 0 auto 16px;
`;

const AgentLoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();
  const { isAgent, loading: agentLoading } = useIsAgent();

  React.useEffect(() => {
    if (!agentLoading && user && isAgent) {
      navigate(AGENT_ROUTES.bookings, { replace: true });
    }
  }, [user, isAgent, agentLoading, navigate]);

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email.trim(),
        password: values.password,
      });

      if (error) throw error;

      if (!data.user) {
        message.error("Login failed");
        return;
      }

      const { data: agentLink } = await supabase
        .from("agent_users")
        .select("user_id")
        .eq("user_id", data.user.id)
        .maybeSingle();

      if (!agentLink) {
        await supabase.auth.signOut();
        message.error(
          "This account is not linked to an agent profile. Contact E-Freight Forwarding for access."
        );
        return;
      }

      message.success("Welcome back");
      navigate(AGENT_ROUTES.bookings, { replace: true });
    } catch (err) {
      const authError = err as AuthError;
      message.error(authError.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContainer>
      <StyledCard>
        <Logo src="/logo_black.png" alt="E-Freight Forwarding" />
        <Title level={3} style={{ textAlign: "center", marginBottom: 4 }}>
          Agent portal
        </Title>
        <Paragraph type="secondary" style={{ textAlign: "center", marginBottom: 24 }}>
          Sign in to manage your assigned bookings and containers.
        </Paragraph>
        <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input autoComplete="email" />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Sign in
            </Button>
          </Form.Item>
        </Form>
      </StyledCard>
    </AuthContainer>
  );
};

export default AgentLoginPage;
